import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';

import LiveWindSourceAttribution from 'components/LiveWindSourceAttribution';
import MapControls from 'components/MapControls';
import MapHeader from 'components/MapHeader';
import MapWindStationCards from 'components/MapWindStationCards';
import MapWindStationPins from 'components/MapWindStationPins';
import WindStationCardPlaceholders from 'components/WindStationCardPlaceholders';
import useMap from 'hooks/map/useMap';
import useSyncMapLocationToUrl from 'hooks/map/useSyncMapLocationToUrl';
import useSyncMapLocationToSession from 'hooks/map/useSyncMapLocationToSession';
import useHydrateMapLocation from 'hooks/map/useHydrateMapLocation';
import useWindStationsBoundingBoxQuery from 'hooks/map/useWindStationsBoundingBoxQuery';
import { useUserPermissionStatus } from 'selectors/user';
import mapLayers from 'utils/mapLayers';
import { getWindStationMapPathWithLocationObject } from 'utils/urls/mapPaths';

import styles from './WindStationMap.module.scss';

interface Props {}

const WindStationMap: React.FC<Props> = () => {
  // Be careful changing the order of these hooks, as other hooks may depend on the location
  // set into the store via `useHydrateMapLocation`
  useHydrateMapLocation();
  useSyncMapLocationToSession();
  useSyncMapLocationToUrl(getWindStationMapPathWithLocationObject);

  const [map] = useMap();
  const mapContainer = map?.getContainer();

  const { windStations, windSpeedUnits, hasWindStations, noWindStationsFound, loading, error } =
    useWindStationsBoundingBoxQuery();

  const { hasLiveWindPermissions } = useUserPermissionStatus();

  const segmentProperties = useMemo(() => ({ pageName: 'Wind Stations Map' }), []);

  const [providerAttributions, setProviderAttributions] = useState<string[]>([]);

  useEffect(() => {
    if (!loading) {
      setProviderAttributions([]);
    }
    if (!loading && hasWindStations) {
      windStations?.forEach((station) => {
        if (station?.provider) {
          setProviderAttributions((prev) => [...prev, station.provider]);
        }
      });
    }
  }, [hasWindStations, loading, windStations]);

  return (
    <div className={styles.windStationMap}>
      <MapHeader
        heading="Wind stations"
        hasMapItems={hasWindStations || false}
        loading={loading}
        mapType="windStations"
      />
      <MapControls
        activeMap={mapLayers.WIND_STATIONS}
        className={classNames(styles.slMapLinks, styles.slMapLinksHasWindstationsLeft)}
      />
      {mapContainer &&
        createPortal(
          <MapWindStationPins
            windStations={windStations}
            windSpeedUnits={windSpeedUnits}
            hasLiveWindPermissions={hasLiveWindPermissions}
          />,
          mapContainer,
        )}

      {hasWindStations ? (
        <MapWindStationCards
          windStations={windStations}
          windSpeedUnits={windSpeedUnits}
          hasLiveWindPermissions={hasLiveWindPermissions}
          segmentProperties={segmentProperties}
        />
      ) : (
        <WindStationCardPlaceholders
          numPlaceholders={12}
          noWindStationsFound={noWindStationsFound}
          error={error}
          loading={loading}
        />
      )}
      <Box className={styles.attributions}>
        {providerAttributions.includes('BOM') && <LiveWindSourceAttribution source="BOM" />}
        {providerAttributions.includes('MADIS') && <LiveWindSourceAttribution source="MADIS" />}
      </Box>
    </div>
  );
};

export default WindStationMap;
